/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: DynamicContextsTab.tsx
 ??? Created: 2025-01-21
 ?? Description:
    Dynamic Context management tab for AI Agents.
    Supports document upload (with AI extraction) and manual entry.
----------------------------------------------------------------*/

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Plus, Upload, FileText, Trash2, Edit2, X, AlertCircle,
    FileUp, RefreshCw, ChevronDown, ChevronUp, Eye, Search, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import DocumentEditModal from './DocumentEditModal';
import { getAiProfiles, type AiProfile } from '../../ai-profiles/api/aiProfilesApi';
import {
    getDynamicContexts,
    createDynamicContext,
    updateDynamicContext,
    deleteDynamicContext,
    getContextDocuments,
    uploadContextDocument,
    updateContextDocument,
    processContextDocument,
    deleteContextDocument,
    getDefaultExtractionPrompt,
    type DynamicContext,
    type DynamicContextInput,
    type ContextDocument
} from '../api/aiAgentsService';

import { DynamicContextEditModal } from './DynamicContextEditModal';
import { DynamicContextUploadModal } from './DynamicContextUploadModal';

interface DynamicContextsTabProps {
    agentId: number | string | null;
    isNew: boolean;
    aiProfileId?: number | string | null;
    isDefaultChatbot?: boolean;
}

// Simplified Document Card - only shows info and Edit/Delete buttons
interface DocumentCardProps {
    doc: ContextDocument;
    onEdit: () => void;
    onDelete: () => void;
}

function DocumentCard({ doc, onEdit, onDelete }: DocumentCardProps) {
    return (
        <div className="flex items-center justify-between p-3 border border-border rounded-lg bg-card hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <div>
                    <span className="font-medium">{doc.originalFilename}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                        [{doc.contextsExtracted} contexts]
                    </span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${doc.processingStatus === 'completed' ? 'bg-green-500/20 text-green-500' :
                    doc.processingStatus === 'failed' ? 'bg-red-500/20 text-red-500' :
                        doc.processingStatus === 'processing' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-gray-500/20 text-gray-500'
                    }`}>
                    {doc.processingStatus}
                </span>
            </div>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={onEdit}
                    className="p-1.5 hover:bg-primary/20 text-primary rounded"
                    title="Edit document"
                >
                    <Edit2 className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={onDelete}
                    className="p-1.5 hover:bg-red-500/20 text-red-500 rounded"
                    title="Delete document"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

export default function DynamicContextsTab({ agentId, isNew, aiProfileId }: DynamicContextsTabProps) {
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    // State
    const [contexts, setContexts] = useState<DynamicContext[]>([]);
    const [documents, setDocuments] = useState<ContextDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [editingContext, setEditingContext] = useState<DynamicContext | null>(null);
    const [expandedDocuments, setExpandedDocuments] = useState<Set<number>>(new Set());
    const [deleteDocumentId, setDeleteDocumentId] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [editingDocument, setEditingDocument] = useState<ContextDocument | null>(null);
    const [generalProfiles, setGeneralProfiles] = useState<AiProfile[]>([]);

    // Search and pagination state
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteContextId, setDeleteContextId] = useState<number | null>(null);
    const [selectedSource, setSelectedSource] = useState<'manual' | number>('manual');
    const pageSize = 10;

    // Form state for add/edit modal
    const [formData, setFormData] = useState<DynamicContextInput>({
        contextKey: '',
        contextName: '',
        contextContent: '',
        triggerKeywords: [],
        priority: 50,
        reusePolicy: 'reusable',
        enabled: true
    });
    const [keywordInput, setKeywordInput] = useState('');

    // Document upload state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [extractionPrompt, setExtractionPrompt] = useState('');
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load data
    const loadData = useCallback(async () => {
        if (!agentId) return;

        setLoading(true);
        try {
            const [contextsData, documentsData, profilesResponse] = await Promise.all([
                getDynamicContexts(agentId),
                getContextDocuments(agentId),
                getAiProfiles({ type: 'general' })
            ]);
            setContexts(contextsData);
            setDocuments(documentsData);
            // Set general type profiles from response
            setGeneralProfiles(profilesResponse.data);
        } catch (error) {
            showError(error.message || 'Failed to load contexts');
        } finally {
            setLoading(false);
        }
    }, [agentId, showError]);

    useEffect(() => {
        if (agentId && !isNew) {
            loadData();
        }
    }, [agentId, isNew, loadData]);

    // Load default extraction prompt
    useEffect(() => {
        const loadDefaultPrompt = async () => {
            try {
                const prompt = await getDefaultExtractionPrompt();
                setExtractionPrompt(prompt);
            } catch (error) {
                console.error('Failed to load default extraction prompt:', error);
            }
        };
        loadDefaultPrompt();
    }, []);

    // Listen for events from parent component (header buttons)
    useEffect(() => {
        const handleOpenAddModal = () => {
            resetForm();
            setEditingContext(null);
            setShowAddModal(true);
        };

        window.addEventListener('openAddContextModal', handleOpenAddModal);
        return () => window.removeEventListener('openAddContextModal', handleOpenAddModal);
    }, []);

    // Handlers
    const handleAddKeyword = () => {
        if (keywordInput.trim() && !formData.triggerKeywords?.includes(keywordInput.trim())) {
            setFormData(prev => ({
                ...prev,
                triggerKeywords: [...(prev.triggerKeywords || []), keywordInput.trim()]
            }));
            setKeywordInput('');
        }
    };

    const handleRemoveKeyword = (keyword: string) => {
        setFormData(prev => ({
            ...prev,
            triggerKeywords: (prev.triggerKeywords || []).filter(k => k !== keyword)
        }));
    };

    const handleSaveContext = async () => {
        if (!agentId) {
            showError('Please save the agent first');
            return;
        }

        try {
            if (editingContext) {
                await updateDynamicContext(agentId, editingContext.id, formData);
                showSuccess('Context updated successfully');
            } else {
                await createDynamicContext(agentId, formData);
                showSuccess('Context created successfully');
            }
            setShowAddModal(false);
            setEditingContext(null);
            resetForm();
            loadData();
        } catch (error) {
            showError(error.message || 'Failed to save context');
        }
    };

    const handleDeleteContextClick = (contextId: number) => {
        setDeleteContextId(contextId);
    };

    const confirmDeleteContext = async () => {
        if (!agentId || !deleteContextId) return;
        const contextId = deleteContextId;
        setDeleteContextId(null);
        try {
            await deleteDynamicContext(agentId, contextId);
            showSuccess('Context deleted successfully');
            loadData();
        } catch (error) {
            showError(error.message || 'Failed to delete context');
        }
    };

    const handleEditContext = (context: DynamicContext) => {
        setEditingContext(context);
        setFormData({
            contextKey: context.contextKey,
            contextName: context.contextName,
            contextContent: context.contextContent,
            triggerKeywords: context.triggerKeywords,
            priority: context.priority,
            reusePolicy: context.reusePolicy,
            enabled: context.enabled
        });
        setShowAddModal(true);
    };

    const handleToggleEnabled = async (context: DynamicContext) => {
        if (!agentId) return;

        try {
            await updateDynamicContext(agentId, context.id, { enabled: !context.enabled });
            loadData();
        } catch (error) {
            showError(error.message || 'Failed to update context');
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const validTypes = ['text/plain', 'text/markdown', 'application/pdf',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!validTypes.includes(file.type) &&
                !file.name.endsWith('.txt') &&
                !file.name.endsWith('.md')) {
                showError('Supported formats: TXT, MD, PDF, DOCX');
                return;
            }
            setSelectedFile(file);
            setShowUploadModal(true);
        }
    };

    const handleUploadDocument = async () => {
        if (!agentId || !selectedFile) return;

        setUploading(true);
        try {
            await uploadContextDocument(agentId, selectedFile, extractionPrompt);
            showSuccess('Document uploaded successfully');
            setShowUploadModal(false);
            setSelectedFile(null);
            loadData();
        } catch (error) {
            showError(error.message || 'Failed to upload document');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDocument = async (documentId: number) => {
        if (!agentId) return;
        setIsDeleting(true);
        try {
            await deleteContextDocument(agentId, documentId);
            showSuccess('Document deleted successfully');
            setDeleteDocumentId(null);
            loadData();
        } catch (error) {
            showError(error.message || 'Failed to delete document');
        } finally {
            setIsDeleting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            contextKey: '',
            contextName: '',
            contextContent: '',
            triggerKeywords: [],
            priority: 50,
            reusePolicy: 'reusable',
            enabled: true
        });
        setKeywordInput('');
    };

    const estimateTokens = (text: string): number => {
        return Math.ceil((text || '').length / 4);
    };

    const toggleDocumentExpanded = (docId: number) => {
        setExpandedDocuments(prev => {
            const next = new Set(prev);
            if (next.has(docId)) {
                next.delete(docId);
            } else {
                next.add(docId);
            }
            return next;
        });
    };

    // Filtered and paginated contexts - first by source, then by search
    const sourceFilteredContexts = useMemo(() => {
        if (selectedSource === 'manual') {
            return contexts.filter(ctx => ctx.sourceType === 'manual');
        } else {
            return contexts.filter(ctx => ctx.sourceDocumentId === selectedSource);
        }
    }, [contexts, selectedSource]);

    const filteredContexts = useMemo(() => {
        if (!searchQuery.trim()) return sourceFilteredContexts;
        const query = searchQuery.toLowerCase();
        return sourceFilteredContexts.filter(ctx =>
            ctx.contextKey.toLowerCase().includes(query) ||
            ctx.contextName.toLowerCase().includes(query) ||
            ctx.triggerKeywords.some(kw => kw.toLowerCase().includes(query))
        );
    }, [sourceFilteredContexts, searchQuery]);

    const totalPages = Math.ceil(filteredContexts.length / pageSize);
    const paginatedContexts = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredContexts.slice(start, start + pageSize);
    }, [filteredContexts, currentPage, pageSize]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // Get document name for a context
    const getDocumentName = (ctx: DynamicContext) => {
        if (ctx.sourceType !== 'document' || !ctx.sourceDocumentId) return null;
        const doc = documents.find(d => String(d.id) === String(ctx.sourceDocumentId));
        return doc?.originalFilename || 'Unknown';
    };

    // If new agent, show placeholder
    if (isNew) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg">{t('aiAgents.dynamicContext.saveFirst') || 'Save the agent first to manage dynamic contexts'}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Hidden file input for upload from parent header */}
            <input
                type="file"
                id="dynamic-context-file-input"
                ref={fileInputRef}
                className="hidden"
                accept=".txt,.md,.pdf,.docx"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        const validTypes = ['text/plain', 'text/markdown', 'application/pdf',
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                        if (!validTypes.includes(file.type) &&
                            !file.name.endsWith('.txt') &&
                            !file.name.endsWith('.md')) {
                            showError('Supported formats: TXT, MD, PDF, DOCX');
                            return;
                        }
                        setSelectedFile(file);
                        setShowUploadModal(true);
                    }
                }}
            />

            {/* Master-Detail Layout */}
            <div className="flex gap-4">
                {/* Source Listbox (Left Panel) */}
                <div className="w-72 shrink-0">
                    <div className="border border-border rounded-lg bg-card overflow-hidden">
                        <div className="p-3 bg-muted/50 border-b border-border">
                            <span className="text-sm font-medium">{t('aiAgents.dynamicContext.source') || 'Source'}</span>
                        </div>
                        <div className="max-h-[400px] overflow-y-auto">
                            {/* Manual Entry Option */}
                            <div
                                onClick={() => setSelectedSource('manual')}
                                className={`flex items-center gap-3 p-3 cursor-pointer border-b border-border transition-colors ${selectedSource === 'manual'
                                    ? 'bg-primary/10 border-l-2 border-l-primary'
                                    : 'hover:bg-muted/50'
                                    }`}
                            >
                                <Edit2 className="h-5 w-5 text-gray-500" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">Manual Entries</p>
                                    <p className="text-xs text-muted-foreground">
                                        {contexts.filter(c => c.sourceType === 'manual').length} contexts
                                    </p>
                                </div>
                            </div>



                            {/* Document Entries */}
                            {documents.map(doc => (
                                <div
                                    key={doc.id}
                                    onClick={() => setSelectedSource(doc.id)}
                                    className={`flex items-center gap-3 p-3 cursor-pointer border-b border-border last:border-b-0 transition-colors ${selectedSource === doc.id
                                        ? 'bg-primary/10 border-l-2 border-l-primary'
                                        : 'hover:bg-muted/50'
                                        }`}
                                >
                                    <FileText className="h-5 w-5 text-primary" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">{doc.originalFilename}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {doc.contextsExtracted} contexts �
                                            <span className={`ml-1 ${doc.processingStatus === 'completed' ? 'text-green-500' :
                                                doc.processingStatus === 'failed' ? 'text-red-500' : 'text-yellow-500'}`}>
                                                {doc.processingStatus}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <SimpleTooltip content={t('common.edit') || 'Edit'} position="top">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setEditingDocument(doc); }}
                                                className="p-1 hover:bg-primary/20 text-primary rounded"
                                            >
                                                <Edit2 className="h-3 w-3" />
                                            </button>
                                        </SimpleTooltip>
                                        <SimpleTooltip content={t('common.delete') || 'Delete'} position="top">
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setDeleteDocumentId(doc.id); }}
                                                className="p-1 hover:bg-red-500/20 text-red-500 rounded"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        </SimpleTooltip>
                                    </div>
                                </div>
                            ))}

                            {documents.length === 0 && (
                                <div className="p-4 text-center text-sm text-muted-foreground">
                                    No documents uploaded yet
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Contexts Panel (Right) */}
                <div className="flex-1 min-w-0">
                    {/* Header with Source Name and Search */}
                    <div className="flex items-center gap-4 mb-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            {selectedSource === 'manual' ? (
                                <>
                                    <Edit2 className="h-5 w-5 text-gray-500 shrink-0" />
                                    <span className="font-medium truncate">Manual Entries</span>
                                </>
                            ) : (
                                <>
                                    <FileText className="h-5 w-5 text-primary shrink-0" />
                                    <span className="font-medium truncate">
                                        {documents.find(d => String(d.id) === String(selectedSource))?.originalFilename || 'Document'}
                                    </span>
                                </>
                            )}
                        </div>
                        <div className="relative w-64 shrink-0">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder={t('common.search') || 'Search...'}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-1.5 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                        </div>
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {filteredContexts.length} {t('aiAgents.dynamicContext.contextsCount') || 'context(s)'}
                        </span>
                    </div>

                    {/* Contexts Table */}
                    <div className="border border-border rounded-lg overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-muted/50">
                                <tr>
                                    <th className="text-left p-3 text-sm font-medium">{t('aiAgents.dynamicContext.key') || 'Key'}</th>
                                    <th className="text-left p-3 text-sm font-medium">{t('aiAgents.dynamicContext.name') || 'Name'}</th>
                                    <th className="text-left p-3 text-sm font-medium">{t('aiAgents.dynamicContext.triggers') || 'Triggers'}</th>
                                    <th className="text-center p-3 text-sm font-medium">{t('aiAgents.dynamicContext.priority') || 'Priority'}</th>
                                    <th className="text-center p-3 text-sm font-medium">{t('aiAgents.dynamicContext.tokens') || 'Tokens'}</th>
                                    <th className="text-center p-3 text-sm font-medium">{t('common.enabled') || 'Enabled'}</th>
                                    <th className="text-center p-3 text-sm font-medium">{t('common.actions') || 'Actions'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="text-center p-8 text-muted-foreground">
                                            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                                            Loading...
                                        </td>
                                    </tr>
                                ) : paginatedContexts.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center p-8 text-muted-foreground">
                                            {searchQuery ? 'No contexts match your search' : (t('aiAgents.dynamicContext.noContexts') || 'No dynamic contexts yet. Add one manually or upload a document.')}
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedContexts.map(ctx => (
                                        <tr key={ctx.id} className="border-t border-border hover:bg-muted/30">
                                            <td className="p-3">
                                                <span className="font-mono text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                                                    {ctx.contextKey}
                                                </span>
                                            </td>
                                            <td className="p-3 text-sm">{ctx.contextName}</td>
                                            <td className="p-3">
                                                <div className="flex flex-wrap gap-1">
                                                    {ctx.triggerKeywords.slice(0, 3).map(kw => (
                                                        <span key={kw} className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                                            {kw}
                                                        </span>
                                                    ))}
                                                    {ctx.triggerKeywords.length > 3 && (
                                                        <span className="text-xs text-muted-foreground">
                                                            +{ctx.triggerKeywords.length - 3}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-3 text-center text-sm">{ctx.priority}</td>
                                            <td className="p-3 text-center">
                                                <span className={`text-xs ${(ctx.tokenCount || 0) > 120 ? 'text-red-500' : 'text-muted-foreground'
                                                    }`}>
                                                    {ctx.tokenCount || estimateTokens(ctx.contextContent)}
                                                </span>
                                            </td>
                                            <td className="p-3 text-center">
                                                <Toggle
                                                    value={ctx.enabled}
                                                    onChange={() => handleToggleEnabled(ctx)}
                                                />
                                            </td>
                                            <td className="p-3 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditContext(ctx)}
                                                        className="p-1 hover:bg-muted rounded"
                                                    >
                                                        <Edit2 className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteContextClick(ctx.id)}
                                                        className="p-1 hover:bg-red-500/20 text-red-500 rounded"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {filteredContexts.length > pageSize && (
                        <div className="flex items-center justify-between pt-2">
                            <span className="text-sm text-muted-foreground">
                                Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filteredContexts.length)} of {filteredContexts.length}
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 hover:bg-muted rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                                    Math.max(0, currentPage - 3),
                                    Math.min(totalPages, currentPage + 2)
                                ).map(page => (
                                    <button
                                        key={page}
                                        type="button"
                                        onClick={() => setCurrentPage(page)}
                                        className={`px-3 py-1 text-sm rounded ${page === currentPage ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 hover:bg-muted rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            <DynamicContextEditModal
                isOpen={showAddModal}
                isEditing={editingContext !== null}
                formData={formData}
                keywordInput={keywordInput}
                setKeywordInput={setKeywordInput}
                setFormData={setFormData}
                onSave={handleSaveContext}
                onClose={() => { setShowAddModal(false); setEditingContext(null); }}
                t={t}
            />

            {/* Upload Modal */}
            <DynamicContextUploadModal
                isOpen={showUploadModal}
                selectedFile={selectedFile}
                extractionPrompt={extractionPrompt}
                uploading={uploading}
                setExtractionPrompt={setExtractionPrompt}
                onUpload={handleUploadDocument}
                onClose={() => { setShowUploadModal(false); setSelectedFile(null); }}
                t={t}
            />

            {/* Delete Context Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteContextId !== null}
                onClose={() => setDeleteContextId(null)}
                onConfirm={confirmDeleteContext}
                title={t('common.confirmDelete') || 'Confirm Delete'}
                message={t('aiAgents.dynamicContext.confirmDeleteContext', 'Are you sure you want to delete this context?')}
                confirmText={t('common.delete') || 'Delete'}
                cancelText={t('common.cancel') || 'Cancel'}
                type="danger"
            />

            {/* Delete Document Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteDocumentId !== null}
                onClose={() => setDeleteDocumentId(null)}
                onConfirm={() => {
                    if (deleteDocumentId) {
                        handleDeleteDocument(deleteDocumentId);
                    }
                }}
                title={t('common.confirmDelete') || 'Confirm Delete'}
                message={t('aiAgents.dynamicContext.confirmDeleteDocument') || 'This will delete the document and unlink its contexts. Continue?'}
                confirmText={t('common.delete') || 'Delete'}
                cancelText={t('common.cancel') || 'Cancel'}
                type="danger"
                isLoading={isDeleting}
            />

            {/* Document Edit Modal */}
            <DocumentEditModal
                isOpen={editingDocument !== null}
                onClose={() => setEditingDocument(null)}
                document={editingDocument}
                contexts={contexts}
                aiProfiles={generalProfiles}
                defaultPrompt={extractionPrompt}
                onSavePrompt={async (prompt) => {
                    if (!agentId || !editingDocument) return;
                    await updateContextDocument(agentId, editingDocument.id, { extractionPrompt: prompt });
                    loadData();
                }}
                onProcess={async (profileId: number) => {
                    if (!agentId || !editingDocument) {
                        throw new Error('Agent or document not found');
                    }
                    await processContextDocument(agentId, editingDocument.id, profileId);
                    loadData();
                    setEditingDocument(null);
                }}
            />
        </div>
    );
}
